import { ref, computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import { Opportunity, Scan } from '@/types'
import { fetchReport as apiFetchReport } from '@/api/v4/poweredit'
import { fetchOpportunity as apiFetchOpportunity } from '@/api/v4/tracker'
import { fetchExcludedSkills as apiFetchExcludedSkills } from '@/api/v4/excludedSkills'
import useScanReport from '@/composables/useScanReport'
import { FindingCategory, RephrasePopup } from '@/types'
import { useStructuredResumeStore } from './structuredResume'
import { snackBarStore } from './snackBar'

export const usePowerEditStore = defineStore('powerEdit', () => {
  const focusedSkill = ref<any>(null)
  const showDetailsForm = ref(false)
  const showContactInfoForm = ref(false)
  const focusedContactInfo = ref<string | null>(null)
  const resumeStore = useStructuredResumeStore()
  const activeView = ref<'resume' | 'coverLetter' | 'jobDescription'>('resume')
  const isFetchingOpportunity = ref(false)
  const isFetchingReport = ref(false)
  const highlightWordCountErrors = ref(false)
  const scan = ref<Scan | null>(null)
  const opportunity = ref<Opportunity | null>(null)
  const excludedSkills = ref([])
  const { buildReport } = useScanReport()
  const showEmptyCoverLetter = ref(false)
  const rephrasePopup = ref<RephrasePopup>({
    isOpen: false,
    coords: { top: 0, left: 0, right: 0, bottom: 0 },
  })
  // const highlightedSkill = ref<any>(null)

  const highlightToggles = reactive({
    skill: true,
    missingSkill: true,
    alias: true,
    wordToAvoid: false,
    specialCharacter: false,
  })

  const highlightedText = ref<{ range: Range | null; data: any }>({
    range: null,
    data: null,
  })

  const fetchReport = async (opportunityId: number) => {
    isFetchingReport.value = true
    const { data, error } = await apiFetchReport(opportunityId)
    if (!error) {
      scan.value = data
      isFetchingReport.value = false
      if (data.meta?.refetchOpportunity) {
        fetchOpportunity(opportunityId)
      }
    } else {
      snackBarStore().showErrorSnackBar(
        'Unable to load scan report. Please refresh the page to try again.'
      )
    }

    return { data, error }
  }

  const refetchReport = async () => {
    if (opportunity.value) {
      fetchReport(opportunity.value.id)
    }
  }

  const fetchOpportunity = async (opportunityId: number) => {
    isFetchingOpportunity.value = true
    const { data, error } = await apiFetchOpportunity(opportunityId)
    if (error) {
      return console.error(error)
    }
    opportunity.value = data

    if (data.resume?.data) {
      resumeStore.overwriteResume(data.resume.data)
    }

    isFetchingOpportunity.value = false
    resumeStore.resumeRecord = data.resume
  }

  const fetchExcludedSkills = async () => {
    const { data, error } = await apiFetchExcludedSkills()
    if (error) {
      return console.error(error)
    }
    excludedSkills.value = data
  }

  const scanReport = computed(() => {
    if (scan.value) {
      return buildReport(scan.value, [
        'fileNameLength',
        'fileNameCharacters',
        'fileFormat',
      ])
    }
    return null
  })

  const score = computed(() => {
    return scan.value?.matchRate ?? {}
  })

  const scoreHistory = computed(() => {
    return scan.value?.history ?? []
  })

  const hasCoverletter = computed(() => {
    return !!scan.value?.coverLetter
  })

  const editData = computed(() => {
    const op = opportunity.value
    return {
      jobTitle: op?.jobTitle,
      company: op?.company,
      url: op?.url,
    }
  })

  const editDataScore = computed(() => {
    const items = Object.values(editData.value || {})
    const issueCount = items.reduce((issueCount, item) => {
      issueCount += !item ? 1 : 0
      return issueCount
    }, 0)
    const score = Math.round(((items.length - issueCount) / items.length) * 100)
    return {
      score,
      issueCount,
    }
  })

  const combinedScore = computed<any>(() => {
    return {
      ...score.value,
      [FindingCategory.JOB_DETAILS]: editDataScore.value,
    }
  })

  const jdId = computed(() => {
    return scan.value?.metadata?.jobDescription?.id
  })

  const isFetching = computed(
    () => isFetchingOpportunity.value || isFetchingReport.value
  )

  const coverLetter = computed(() => {
    return scan.value?.coverLetter
  })

  const resetStore = () => {
    opportunity.value = null
    isFetchingOpportunity.value = false
    isFetchingReport.value = false
    activeView.value = 'resume'
    resumeStore.resetStore()
  }

  const rephraseScrollListener = () => {
    if (!rephrasePopup.value.isOpen && !rephrasePopup.value.targetPath) return

    // Replace is required to escape the dots in the targetPath
    const el = document.querySelector(
      `[data-target-path=${rephrasePopup.value?.targetPath?.replace(
        /\./g,
        '\\.'
      )}]`
    )
    if (!el) return
    rephrasePopup.value.coords = {
      top: el.getBoundingClientRect().bottom + window.scrollY + 5,
      left:
        el.getBoundingClientRect().left +
        el.getBoundingClientRect().width -
        320,
      right: el.getBoundingClientRect().right,
      bottom: el.getBoundingClientRect().top + window.scrollY,
    }
  }

  const registerRephrasePopupListeners = () => {
    const rightPanel = document.querySelector('.powerEdit__right-panel')
    rightPanel?.addEventListener('scroll', rephraseScrollListener)
  }

  const deRegisterRephrasePopupListeners = () => {
    const rightPanel = document.querySelector('.powerEdit__right-panel')
    rightPanel?.removeEventListener('scroll', rephraseScrollListener)
  }

  const openRephrasePopup = (
    targetPath: string,
    clickedElement: HTMLUnknownElement
  ): void => {
    const rect = clickedElement?.getBoundingClientRect()
    const coords = {
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX + rect.width - 320,
      right: rect.right + window.scrollX,
      bottom: rect.top + window.scrollY - 5,
    }
    rephrasePopup.value = {
      isOpen: true,
      targetPath,
      coords,
    }
  }

  const closePopup = (): void => {
    rephrasePopup.value = {
      isOpen: false,
      targetPath: '',
      coords: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
    }
  }

  const setHighlightedText = (range: Range | null, data: any): void => {
    highlightedText.value.range = range
    highlightedText.value.data = data
  }

  const replaceHighlightedText = async () => {
    let data_path =
      highlightedText.value.range.commonAncestorContainer.parentNode.dataset
        .targetPath
    data_path = data_path.replace('resume.', '')
    const pathArray = data_path
      .split('.')
      .map(part => (!isNaN(part) ? Number(part) : part))

    const currentValue = pathArray.reduce(
      (obj, key) => obj[key],
      resumeStore.resumeRecord.data
    )

    const regex = new RegExp(highlightedText.value.data.regex, 'gi')
    const newValue = currentValue.replace(
      regex,
      highlightedText.value.data.skill?.name || ''
    )

    let target = resumeStore.resumeRecord.data
    for (let i = 0; i < pathArray.length - 1; i++) {
      target = target[pathArray[i]]
    }
    target[pathArray[pathArray.length - 1]] = newValue
    hideReplacedSkill(highlightedText.value.data.skill.name)
  }

  const hideReplacedSkill = (skillName: string) => {
    const skillTypes = ['hard', 'soft']
    for (const type of skillTypes) {
      const skillIndex = scan.value.skills[type].findIndex(
        skill => skill.name === skillName
      )
      if (skillIndex > -1) {
        scan.value.skills[type].splice(skillIndex, 1)
        break
      }
    }
  }

  const hasSkill = (skillName: string) => {
    return [
      ...(scan.value?.skills?.hard ?? []),
      ...(scan.value?.skills?.soft ?? []),
      ...(scan.value?.excludedSkills?.hard ?? []),
      ...(scan.value?.excludedSkills?.soft ?? []),
    ].some(
      skill => skill.name.toLowerCase() === skillName.toLowerCase()
    )
  }

  return {
    fetchReport,
    scan,
    isFetchingOpportunity,
    isFetchingReport,
    isFetching,
    scanReport,
    score: combinedScore,
    scoreHistory,
    hasCoverletter,
    editData,
    jdId,
    highlightWordCountErrors,
    coverLetter,
    fetchOpportunity,
    opportunity,
    activeView,
    resetStore,
    showDetailsForm,
    showContactInfoForm,
    focusedContactInfo,
    refetchReport,
    highlightToggles,
    fetchExcludedSkills,
    excludedSkills,
    rephrasePopup,
    registerRephrasePopupListeners,
    deRegisterRephrasePopupListeners,
    openRephrasePopup,
    closePopup,
    focusedSkill,
    showEmptyCoverLetter,
    highlightedText,
    setHighlightedText,
    replaceHighlightedText,
    hideReplacedSkill,
    hasSkill,
  }
})
